import React, { act, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TableColumnsFilterComponent, Tables } from '../../../../../Components';
import {
  TableActions,
  ColumnsFilterPagesEnum,
  TableFilterTypesEnum,
} from '../../../../../Enums';
import { ActiveItemActions } from '../../../../../store/ActiveItem/ActiveItemActions';
import { ClientsTrackingTableHeaderData } from './ClientsTrackingTableHeaderData';
import { TableColumnsFilterActions } from '../../../../../store/TableColumnsFilter/TableColumnsFilterActions';
import { ClientsTrackingManagementDialog } from '../ClientsTrackingManagementDialog/ClientsTrackingManagementDialog';
import CreateLeadDialog from '../../../../../Components/CreateLeadDialog/CreateLeadDialog';
import {
  showError,
  showSuccess,
  sideMenuComponentUpdate,
  sideMenuIsOpenUpdate,
  returnPropsByPermissions 
} from '../../../../../Helper';
import ClientTrackingSideCard from './ClientTrackingSideCard';
import ClientTrackingAddReceiptDialog from '../ClientTrackingAddReceiptDialog/ClientTrackingAddReceiptDialog';
import AddActivityDialog from '../Components/AddActivityDialog';
import { CreateVendorClientTrackingActivity } from '../../../../../Services';
import { ClientsTrackingPermissions  } from '../../../../../Permissions' ; 

export const ClientsTrackingTableComponent = ({
  filter,
  pathName,
  clientTrackingTableFilter,
  setFilter,
  detailsClientTrackingList,
  parentTranslationPath,
  isClearFiltersClicked,
  setIsClearFiltersClicked,
  setSortBy,
  reloadData,
  translationPath,
}) => {

  const dispatch = useDispatch();
  const { t } = useTranslation(parentTranslationPath);
  const [tableColumns, setTableColumns] = useState([]);
  const [isOpenCreateLeadDialog, setIsOpenCreateLeadDialog] = useState(false);
  const [openAddActivityDialog, setOpenAddActivityDialog] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [isAddNewReceiptDialogOpen, setIsAddNewReceiptDialogOpen] =
    useState(false);
  const [isOpenClientTrackingDialog, setIsOpenClientTrackingDialog] =
    useState(false);
  const [vendorClientTrackingId, setVendorClientTrackingId] = useState(null);
  const [tableFilterData, setTableFilterData] = useState([]);
  const [selectedTableFilterColumns, setSelectedTableFilterColumns] = useState(
    ClientsTrackingTableHeaderData(t)
      .filter((item) => item.isDefaultFilterColumn)
      .map((column) => column?.id)
  );
  const tableColumnsFilterResponse = useSelector(
    (state) => state.TableColumnsFilterReducer
  );


  const tableActionClicked = useCallback(
    async (actionEnum, item, focusedRow, event) => {
      event.stopPropagation();
      event.preventDefault();
      setActiveItem(item);
      dispatch(ActiveItemActions.activeItemRequest(item));
      if (
        actionEnum === TableActions.openFile.key &&
        item.vendorClientTrackingId
      ) {
        setIsOpenClientTrackingDialog(true);
        setVendorClientTrackingId(item.vendorClientTrackingId);
      } else if (actionEnum === TableActions.createLeadText.key) {
        setIsOpenCreateLeadDialog(true);
      } else if (actionEnum === TableActions.addReceiptDocument.key) {
        setIsAddNewReceiptDialogOpen(true);
      } else if (actionEnum === TableActions.addActivity.key) {
        setOpenAddActivityDialog(true);
      }
    },
    [dispatch, pathName]
  );

  const onSaveAddActivityDialog = async (data) => {
    try {
      data.activityTypeId = data.activityType.activityTypeId;
      await CreateVendorClientTrackingActivity(data);
      showSuccess('create successfully');
      setOpenAddActivityDialog(false);

      // refresh();
    } catch (error) {
      showError('create error');
    } finally {
      // setOpenActivitiesDialog(false);
    }
  };

  const onPageIndexChanged = (pageIndex) => {
    setFilter((item) => ({ ...item, pageIndex }));
  };

  const onPageSizeChanged = (pageSize) => {
    setFilter((item) => ({ ...item, pageIndex: 0, pageSize }));
  };

  useEffect(() => {
    if (
      tableColumnsFilterResponse &&
      tableColumnsFilterResponse[ColumnsFilterPagesEnum.ClientsTracking.key]
    ) {
      setSelectedTableFilterColumns(
        tableColumnsFilterResponse &&
          tableColumnsFilterResponse[ColumnsFilterPagesEnum.ClientsTracking.key]
      );
    }
  }, [tableColumnsFilterResponse]);

  useEffect(() => {
    setTableColumns([
      ...ClientsTrackingTableHeaderData(t, handleRowClick)
        .filter(
          (item) =>
            selectedTableFilterColumns.findIndex(
              (element) => element === item?.id
            ) !== -1
        )
        .map((field) => ({
          id: field?.id || null,
          key: field?.input || null,
          isDate: field?.isDate || false,
          label: field?.label || field.input,
          input: field?.input || field.searchableKey,
          isNumber: field?.fieldType === 'number' || field?.isNumber || false,
          isSortable: field?.isSortable || false,
          isHiddenFilter: field?.isHiddenFilter || false,
          isDefaultFilterColumn: field?.isDefaultFilterColumn || false,
          searchableKey: field?.searchableKey,
          displayPath: field?.displayPath || field.input || null,
          fieldType: field?.fieldType || null,
          component:
            field?.component ||
            ((item) => <div>{item[field.input] || ''}</div>),
          positiveNumber: field.positiveNumber || false,
          textInputType:
            field.textInputType ||
            (field.isNumber ? 'number' : 'text') ||
            (field.fieldType === 'number' ? 'number' : 'text'),
          textInputMax: field.textInputMax,
          textInputMin: field.textInputMin,
        })),
    ]);
  }, [selectedTableFilterColumns, ClientsTrackingTableHeaderData]);

  const onFilterValuesChanged = (newValue) => {
    const localFilterDto = filter || {};
    if (newValue) {
      Object.values(newValue)
        .filter((item) => item.searchableKey || item.displayPath)
        .map((item) => {
          if (localFilterDto[item.displayPath])
            localFilterDto[item.displayPath] =
              item?.value && item.value !== '' ? item.value : null;
          else if (item.value)
            localFilterDto[item.displayPath] =
              item?.value && item.value !== '' ? item.value : null;
        });
    }
    setFilter({
      ...filter,
      ...localFilterDto,
      pageIndex: 0,
    });
  };

  const focusedRowChanged = (_rowIndex, item) => {
    if (item) {
      sideMenuIsOpenUpdate(true);
      setActiveItem(item);
    }
  };

  const handleRowClick = (item, actionName = null) => {
    setActiveItem(item);
    if (actionName === 'receipts') {
      setIsAddNewReceiptDialogOpen(true);
    }
  };

  useEffect(() => {
    setTableFilterData(
      tableColumns.map((column) => ({
        key: column.key || column.fieldKey || column.id,
        filterType:
          (column.isDate && TableFilterTypesEnum.datePicker.key) ||
          (column.withSelectFilter && TableFilterTypesEnum.selectOption.key) ||
          (column.isNumber && TableFilterTypesEnum.numberInput.key) ||
          TableFilterTypesEnum.textInput.key,
        isHiddenFilter: column.isHiddenFilter,
        textInputType: column.textInputType,
        textInputMax: column.textInputMax,
        textInputMin: column.textInputMin,
        displayPath: column.input,
        isNumerInput: column.isNumber,
        positiveNumber: column.positiveNumber || false,
      }))
    );
  }, [tableColumns]);

  useEffect(() => {
    if (activeItem) {
      sideMenuComponentUpdate(
        <ClientTrackingSideCard
          key={activeItem.vendorClientTrackingId}
          {...activeItem}
        />
      );
    } else {
      sideMenuIsOpenUpdate(false);
      sideMenuComponentUpdate(null);
    }
    return () => {
      sideMenuComponentUpdate(null);
    };
  }, [activeItem]);

   const getTableActionsWithPermissions = () => {
     let list = [];
      if (
       returnPropsByPermissions(
         ClientsTrackingPermissions.AddReceiptDocumentForClientTracking.permissionsId
       )
     )
       list.push({
        enum: TableActions.addReceiptDocument.key,
      });

     
     if (
       returnPropsByPermissions(
         ClientsTrackingPermissions.CreateLeadForClientTracking.permissionsId
       )
     )
       list.push({ enum: TableActions.createLeadText.key });
     if (
       returnPropsByPermissions(
         ClientsTrackingPermissions.AddActivityForClientTracking.permissionsId
       )
     )
       list.push({
         enum: TableActions.addActivity.key,
       });
      if(returnPropsByPermissions(
         ClientsTrackingPermissions.OpenClientTracking.permissionsId
       )){
         list.push({
          enum: TableActions.openFile.key,
       });
      }
     return list;
   };

  useEffect(() => {
    if (activeItem) {
      setActiveItem(null);
    }
  }, [detailsClientTrackingList.result]);

  return (
    <div className='w-100 px-3'>
      <TableColumnsFilterComponent
        columns={ClientsTrackingTableHeaderData(t)
          .filter((item) => !item.isHidden)
          .map((item) => ({
            key: item.id || item.input,
            value: item.label || item.input,
          }))}
        selectedColumns={selectedTableFilterColumns}
        onSelectedColumnsChanged={(newValue) => {
          setSelectedTableFilterColumns(newValue);
          let localTableColumnsFilterResponse = tableColumnsFilterResponse;
          if (localTableColumnsFilterResponse)
            localTableColumnsFilterResponse[
              ColumnsFilterPagesEnum.ClientsTracking.key
            ] = newValue;
          else {
            localTableColumnsFilterResponse = {
              [ColumnsFilterPagesEnum.ClientsTracking.key]: newValue,
            };
          }
          dispatch(
            TableColumnsFilterActions.TableColumnsFilterRequest(
              localTableColumnsFilterResponse
            )
          );
        }}
        parentTranslationPath={parentTranslationPath}
      />
      <Tables
        data={detailsClientTrackingList.result}
        headerData={tableColumns}
        filterData={tableFilterData}
        onPageIndexChanged={onPageIndexChanged}
        onPageSizeChanged={onPageSizeChanged}
        defaultActions={getTableActionsWithPermissions()}
        actionsOptions={{
          onActionClicked: tableActionClicked,
        }}
        itemsPerPage={filter.pageSize}
        activePage={filter.pageIndex}
        parentTranslationPath={parentTranslationPath}
        totalItems={detailsClientTrackingList.totalCount}
        isWithFilter
        onFilterValuesChanged={onFilterValuesChanged}
        filterValues={clientTrackingTableFilter}
        setSortBy={setSortBy}
        FilterDisabledButton={true}
        setIsClearFiltersClicked={setIsClearFiltersClicked}
        isClearFiltersClicked={isClearFiltersClicked}
        focusedRowChanged={focusedRowChanged}
      />

      {isOpenClientTrackingDialog && (
        <ClientsTrackingManagementDialog
          parentTranslationPath={parentTranslationPath}
          translationPath={translationPath}
          isOpen={isOpenClientTrackingDialog}
          onClose={() => {
            setIsOpenClientTrackingDialog(false);
            setVendorClientTrackingId(null);
          }}
          onSave={(updateData) => {
            setIsOpenClientTrackingDialog(false);
            setVendorClientTrackingId(null);
            if (updateData) {
              reloadData();
              setIsOpenClientTrackingDialog(false);
              setVendorClientTrackingId(null);
            }
          }}
          vendorClientTrackingId={vendorClientTrackingId}
        />
      )}
      <CreateLeadDialog
        open={isOpenCreateLeadDialog}
        setOpen={setIsOpenCreateLeadDialog}
        contactId={activeItem?.contactId}
      />
      {isAddNewReceiptDialogOpen && (
        <ClientTrackingAddReceiptDialog
          isOpen={isAddNewReceiptDialogOpen}
          activeItem={activeItem}
          onClose={() => {
            setIsAddNewReceiptDialogOpen(false);
          }}
          parentTranslationPath={parentTranslationPath}
        />
      )}
      {openAddActivityDialog && activeItem && (
        <AddActivityDialog
          open
          onClose={() => setOpenAddActivityDialog(false)}
          onSave={onSaveAddActivityDialog}
          contactId={activeItem?.contactId}
          vendorClientTrackingId={activeItem?.vendorClientTrackingId}
        />
      )}
    </div>
  );
};
